import React, { useState, useEffect, useCallback } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Badge, Box, IconButton, useMediaQuery } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay } from '@mui/x-date-pickers';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { CURRENT_DATE } from 'store/actions';
import { useDispatch, useSelector } from 'react-redux';

const events = [new Date('2024-01-01'), new Date('2024-01-02')];

const ServerDay = React.memo(({ highlightedDays = [], day, outsideCurrentMonth, ...other }) => {
  const isSelected = !outsideCurrentMonth && highlightedDays.includes(day.getDate());
  const isDisabled = day > new Date(); // Disable future dates

  return (
    <Badge
      key={day.toString()}
      overlap="circular"
      sx={isSelected ? { margin: '2px', borderRadius: '50%' } : undefined}
      badgeContent={isSelected ? '' : undefined}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      variant="dot"
      color={isSelected ? 'primary' : undefined}
    >
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        disabled={isDisabled}
        sx={{
          '&.Mui-disabled': {
            color: 'text.disabled',
            opacity: 0.5
          }
        }}
      />
    </Badge>
  );
});

const DatePickerStore = ({ sx }) => {
  const date = useSelector((state) => state.customization.currentDate);
  const [calendar, setCalendar] = useState(new Date(date));
  const [highlightedDays, setHighlightedDays] = useState([]);
  const [open, setOpen] = useState(false);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const updateHighlightedDays = useCallback((date) => {
    const daysToHighlight = events
      .filter((event) => event.getMonth() === date.getMonth() && event.getFullYear() === date.getFullYear())
      .map((event) => event.getDate());

    setHighlightedDays(daysToHighlight);
  }, []);

  useEffect(() => {
    updateHighlightedDays(calendar);
  }, [calendar, updateHighlightedDays]);

  const handleCalendarToggle = () => {
    setOpen(!open);
  };

  const handleDateChange = (newValue) => {
    if (newValue && newValue <= new Date()) {
      // Only allow dates in the past
      setCalendar(newValue);
      updateHighlightedDays(newValue);
      dispatch({ type: CURRENT_DATE, currentDate: newValue });
    }
  };

  return (
    <div className="flex items-center justify-end gap-2 w-full">
      {isSmallScreen ? (
        <>
          <IconButton onClick={handleCalendarToggle}>
            <CalendarTodayIcon sx={{ fontSize: '1.5rem', color: 'black' }} />
          </IconButton>

          {open && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                open={open}
                onClose={() => setOpen(false)}
                value={calendar}
                onChange={handleDateChange}
                slots={{ day: ServerDay }}
                slotProps={{
                  day: { highlightedDays }
                }}
                shouldDisableDate={(day) => day > new Date()} // Disable future dates
                format="dd/MM/yyyy"
                disableTextInput
                sx={sx}
              />
            </LocalizationProvider>
          )}
        </>
      ) : (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DatePicker
              value={calendar}
              onChange={handleDateChange}
              slots={{ day: ServerDay }}
              slotProps={{
                day: { highlightedDays }
              }}
              shouldDisableDate={(day) => day > new Date()} // Disable future dates
              format="dd/MM/yyyy"
              ssx={{
                '& .MuiInputBase-root': {
                  height: '40px',
                  fontSize: { xs: '0.75rem', sm: '0.75rem', xl: '1rem' },
                  width: { xs: '120px', sm: '120px', xl: '160px' }
                }
              }}
            />
          </Box>
        </LocalizationProvider>
      )}
    </div>
  );
};

export default DatePickerStore;
